import { useCallback, useRef, useState } from 'preact/hooks';
import api from '../api/api';
import { toast } from 'react-toastify';
import { objectToFormData } from '../helpers/object-to-form-data';

export function usePut(
    endpoint,
    {
        asFormData = false,
        autoCancelPrevious = true,
        successMessage = 'Successfully captured data!',
    } = {}
) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const controllerRef = useRef(null);

    const cancel = useCallback(() => {
        try {
            controllerRef.current?.abort();
        } finally {
            controllerRef.current = null;
        }
    }, []);

    const put = useCallback(
        async (body, config = {}) => {
            if (autoCancelPrevious && controllerRef.current) {
                controllerRef.current.abort();
                controllerRef.current = null;
            }

            const controller = new AbortController();
            controllerRef.current = controller;

            setIsSubmitting(true);
            setError(null);
            setData(null);
            try {
                let payload = body;
                let headers = config.headers || {};

                if (
                    asFormData ||
                    (body &&
                        Object.values(body).some((v) => v instanceof File || v instanceof Blob))
                ) {
                    payload = objectToFormData(body);
                    headers = {
                        ...headers,
                    };
                }

                const response = await api.put(endpoint, payload, {
                    ...config,
                    headers,
                    signal: config.signal ?? controller.signal,
                });

                // For PUT requests, success is indicated by status code, not response data
                setData(response.data || { success: true });

                toast.success(successMessage);

                return response.data || { success: true };
            } catch (err) {
                toast.error(err?.response?.data?.detail ?? err?.message ?? 'Something went wrong!');
                if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
                    return Promise.reject(err);
                }

                setError(err);
                throw err;
            } finally {
                setIsSubmitting(false);
            }
        },
        [endpoint, asFormData, autoCancelPrevious]
    );

    return { put, cancel, isSubmitting, error, data };
}
